<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4">
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-xl font-semibold text-black">Approve Transaction</h3>
        <button
          class="btn btn-sm btn-ghost text-black"
          @click="$emit('close')"
        >
          ✕
        </button>
      </div>

      <!-- Transaction Details -->
      <div class="mb-6">
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p class="text-sm text-gray-600">Amount to Send</p>
            <p class="text-lg font-semibold">{{ transaction.lrs_amount }} LRS</p>
          </div>
          <div>
            <p class="text-sm text-gray-600">Current Wallet Balance</p>
            <p class="text-lg font-semibold" :class="{'text-red-600': isBalanceInsufficient}">
              {{ walletBalance }} LRS
            </p>
          </div>
        </div>
      </div>

      <!-- Wallet Address -->
      <div class="mb-6">
        <p class="text-sm text-gray-600 mb-2">Larissa Wallet Address</p>
        <div class="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
          <code class="text-sm flex-1 font-mono">{{ walletAddress }}</code>
          <button
            class="btn btn-sm btn-ghost"
            @click="copyAddress"
            :class="{'btn-success': copied}"
          >
            {{ copied ? '✓ Copied' : 'Copy' }}
          </button>
        </div>
      </div>

      <!-- QR Code -->
      <div class="mb-6 flex justify-center">
        <div class="p-4 bg-white rounded-lg shadow">
          <QRCode :value="walletAddress" :size="200" level="H" />
        </div>
      </div>

      <!-- Add this after the QR Code section -->
      <div v-if="txHash" class="mb-6">
        <p class="text-sm text-gray-600 mb-2">Transaction Details</p>
        <div class="bg-gray-50 p-3 rounded-lg space-y-2">
          <!-- Transaction Hash -->
          <div class="flex items-center justify-between">
            <span class="text-sm">Transaction Hash:</span>
            <div class="flex items-center space-x-2">
              <code class="text-xs font-mono">{{ txHash }}</code>
              <button
                class="btn btn-xs btn-ghost"
                @click="copyTxHash"
                :class="{'btn-success': txHashCopied}"
              >
                {{ txHashCopied ? '✓' : 'Copy' }}
              </button>
            </div>
          </div>

          <!-- Transaction Status with Animation -->
          <div class="flex flex-col items-center py-4">
            <!-- Pending Animation -->
            <div v-if="txStatus === 'pending'" class="flex flex-col items-center">
              <div class="w-16 h-16 relative">
                <div class="w-16 h-16 border-4 border-purple-200 rounded-full"></div>
                <div class="w-16 h-16 border-4 border-purple-600 rounded-full border-t-transparent animate-spin absolute top-0"></div>
              </div>
              <p class="mt-4 text-purple-600 font-medium">Transaction Pending</p>
              <p class="text-sm text-gray-500 mt-1">Please wait while the transaction is being confirmed...</p>
            </div>

            <!-- Success Animation -->
            <div v-if="txStatus === 'confirmed'" class="flex flex-col items-center">
              <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg class="w-10 h-10 text-green-600 animate-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p class="mt-4 text-green-600 font-medium">Transaction Confirmed!</p>
              <p class="text-sm text-gray-500 mt-1">Your transaction has been successfully confirmed.</p>
            </div>

            <!-- Failed Animation -->
            <div v-if="txStatus === 'failed'" class="flex flex-col items-center">
              <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg class="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <p class="mt-4 text-red-600 font-medium">Transaction Failed</p>
              <p class="text-sm text-gray-500 mt-1">There was an error processing your transaction.</p>
            </div>
          </div>

          <!-- Explorer Link -->
          <a
            v-if="txHash"
            :href="`${explorerUrl}/tx/${txHash}`"
            target="_blank"
            class="text-purple-600 hover:text-purple-800 text-sm block text-center mt-2"
          >
            View on Explorer
          </a>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end space-x-3">
        <button
          class="btn btn-ghost"
          @click="$emit('close')"
          :disabled="isSending"
        >
          {{ txHash ? 'Close' : 'Cancel' }}
        </button>
        <button
          v-if="!txHash"
          class="btn btn-primary"
          :disabled="isBalanceInsufficient || isSending"
          @click="sendLRS"
        >
          <span v-if="isSending">
            <span class="loading loading-spinner loading-sm"></span>
            Sending...
          </span>
          <span v-else>
            Send LRS
          </span>
        </button>
      </div>

      <!-- Warning Message -->
      <div v-if="isBalanceInsufficient" class="mt-4 text-red-600 text-sm">
        Insufficient balance to complete this transaction
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import QRCode from 'qrcode.vue'
import { ethers } from 'ethers'
import { useEnv } from '../composables/useEnv'

const props = defineProps<{
  show: boolean
  transaction: any
}>()

const emit = defineEmits(['close', 'completed'])

// Use environment variables
const RPC_URL = import.meta.env.VITE_RPC_URL
const CHAIN_ID = parseInt(import.meta.env.VITE_CHAIN_ID)
const explorerUrl = computed(() => import.meta.env.VITE_EXPLORER_URL)
const { apiUrl } = useEnv()

// Add debug mode with more verbose logging
const DEBUG = true
const log = (...args: any[]) => {
  if (DEBUG) {
    console.log('[Larissa Network]', ...args)
  }
}

const walletAddress = ref('')
const walletBalance = ref(0)
const isSending = ref(false)
const copied = ref(false)
const txHash = ref('')
const txStatus = ref('')
const txHashCopied = ref(false)

// Initialize provider
const provider = new ethers.JsonRpcProvider(RPC_URL, {
  name: 'Larissa Network',
  chainId: CHAIN_ID,
  // networkId: CHAIN_ID
})

// Remove wallet initialization from frontend
// We'll use the backend endpoint for all transactions

// Update getWalletInfo to only get balance
const getWalletInfo = async () => {
  try {
    // Get network info
    const network = await provider.getNetwork()
    log('Connected to Larissa Network:', network)

    // Get admin wallet info from backend
    const token = localStorage.getItem('adminToken')
    if (!token) {
      throw new Error('No auth token found')
    }

    const response = await fetch(`${apiUrl}/api/admin/wallet-info`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get wallet info')
    }

    const data = await response.json()
    walletAddress.value = data.address
    walletBalance.value = Number(ethers.utils.formatEther(data.balance))

    log('Admin wallet address:', data.address)
    log('Admin wallet balance:', walletBalance.value, 'LRS')

    return data.address
  } catch (error) {
    console.error('Error getting wallet info:', error)
    walletBalance.value = 0
    throw error
  }
}

// Computed Properties
const isBalanceInsufficient = computed(() => {
  return walletBalance.value < (props.transaction.lrs_amount || 0)
})

// Methods
const copyAddress = async () => {
  try {
    await navigator.clipboard.writeText(walletAddress.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy:', error)
  }
}

// Update checkNetwork function with new chain ID
const checkNetwork = async () => {
  try {
    const network = await provider.getNetwork()
    log('Connected to testnet:', network)
    if (network.chainId !== CHAIN_ID) {
      throw new Error(`Wrong network - Expected chain ID: ${CHAIN_ID}`)
    }
  } catch (error) {
    console.error('Network error:', error)
    throw new Error('Failed to connect to Larissa Testnet')
  }
}

// Add copy transaction hash method
const copyTxHash = async () => {
  try {
    await navigator.clipboard.writeText(txHash.value)
    txHashCopied.value = true
    setTimeout(() => {
      txHashCopied.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy tx hash:', error)
  }
}

// Update sendLRS function
const sendLRS = async () => {
  if (isBalanceInsufficient.value) return;
  isSending.value = true;
  txStatus.value = 'pending';

  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/send-lrs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      },
      body: JSON.stringify({
        amount: props.transaction.lrs_amount,
        recipientAddress: props.transaction.wallet_address,
        transactionId: props.transaction.id
      })
    });

    if (!response.ok) throw new Error('Failed to send LRS');

    const data = await response.json();
    txHash.value = data.txHash;
    txStatus.value = 'confirmed';

    await getWalletInfo();
    emit('completed');

  } catch (error) {
    console.error('Error sending LRS:', error);
    txStatus.value = 'failed';
    alert(`Failed to send LRS: ${error.message || 'Unknown error'}`);
  } finally {
    isSending.value = false;
  }
}

// Update onMounted with better error handling
onMounted(async () => {
  try {
    await checkNetwork()
    await getWalletInfo()

    const interval = setInterval(async () => {
      try {
        await getWalletInfo()
      } catch (error) {
        console.error('Error updating wallet info:', error)
      }
    }, 30000)

    onUnmounted(() => {
      clearInterval(interval)
    })
  } catch (error) {
    console.error('Error in onMounted:', error)
    if (error.message.includes('auth token')) {
      alert('Please log in again')
      // Handle logout/redirect
    } else {
      alert('Failed to connect to Larissa Network. Please check your connection.')
    }
  }
})
</script>

<style scoped>
@keyframes success {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-success {
  animation: success 0.5s ease-out forwards;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>